/**
 * RegenerateButton — small inline button next to outline sections that
 * calls /api/pulpit/regenerate-section and replaces the section's text.
 *
 * If `isDirty` is true, the click will surface an AlertDialog warning
 * the user that their inline edits will be overwritten.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getErrorMessage } from "../../utils/errorHandler";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

export default function RegenerateButton({
  section,
  context,
  pointIndex = null,
  hint = null,
  onResult,
  size = "xs",
  testId,
  label = "Regenerate",
  isDirty = false,
  t = (k) => k,
}) {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const doRegenerate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/pulpit/regenerate-section`,
        { section, context, hint, point_index: pointIndex },
        { headers: authHeader() }
      );
      onResult && onResult(res.data.text, res.data);
      toast.success(`${t("regenerated") || "Regenerated"} ${section}`);
    } catch (err) {
      toast.error(getErrorMessage(err, t("regenerate_failed") || "Regenerate failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      doRegenerate();
    }
  };

  const dotTestId = testId || `regen-${section}${pointIndex !== null ? `-${pointIndex}` : ""}`;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        disabled={loading}
        className={`h-6 px-2 text-[10px] text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 ${size === "xs" ? "text-[10px]" : ""} ${isDirty ? "ring-1 ring-amber-500/40" : ""}`}
        data-testid={dotTestId}
        data-dirty={isDirty ? "true" : "false"}
        title={isDirty
          ? (t("regenerate_dirty_title") || `Regenerate ${section} (will discard your edits)`)
          : `${t("regenerate") || "Regenerate"} ${section}`}
      >
        {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
        {label}
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${dotTestId}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle data-testid={`${dotTestId}-confirm-title`}>
              {t("regenerate_confirm_title") || "Discard your edits?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("regenerate_confirm_body") ||
                "This section has unsaved edits. Regenerating will overwrite your changes with new AI output. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${dotTestId}-confirm-cancel`}>
              {t("cancel") || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${dotTestId}-confirm-action`}
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => {
                setConfirmOpen(false);
                doRegenerate();
              }}
            >
              {t("regenerate_confirm_action") || "Regenerate anyway"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
